/**
 * Les écrans, et le passage de l'un à l'autre.
 *
 * Pas de routeur : quelques états suffisent, et une dépendance de plus se paierait au
 * premier affichage. C'est aussi ici que sont chargées les tables d'IA — **en
 * différé**, comme le WASM de la physique (§ 7.3). Elles ne servent qu'en partie, et
 * les faire porter au premier chargement retarderait l'affichage de la table pour
 * quelqu'un qui vient juste lancer trois dés.
 *
 * ## Deux façons de jouer hors ligne, un seul écran de partie
 *
 * La soirée libre et la campagne passent toutes deux par un `Tournoi` : la première
 * en format soirée, la seconde en format simple — sauf le dernier comptoir. Tout ce
 * qui suit — actions, garde, scène, annonces, podium — ne sait pas laquelle des deux
 * il sert, et c'est ce qui a rendu le lot 7 court.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  additionner, caveSuivante, chargerTables, comptoirDe, enregistrer, profilDe,
  ouvrirTournoi, placesDuComptoir, progressionNeuve, REGLES_MAISON,
  statistiquesVides,
  type Comptoir, type Niveau, type Progression, type Statistiques, type Tables,
  type Tournoi,
} from "@zinc421/shared";

import { Campagne } from "./campagne/Campagne.js";
import {
  chargerProgression, chargerReprise, chargerSon, coffreNavigateur, oublierReprise,
  sauverProgression, sauverReprise, sauverSon, type Coffre,
} from "./campagne/coffre.js";
import {
  issueDeComptoir, libelleSuiteComptoir, MA_PLACE, titreDeCave,
} from "./campagne/issue.js";
import { useTextes } from "./i18n/contexte.js";
import { Accueil } from "./jeu/Accueil.js";
import { nomDeLEtape } from "./jeu/libelles.js";
import { Partie } from "./jeu/Partie.js";
import { libelleReprise, type EnCours } from "./jeu/reprise.js";
import type { Textes } from "./i18n/index.js";
import { nomPropre, placesDeLaTable } from "./jeu/session.js";
import { BacASable } from "./prototype/BacASable.js";
import { usePartieEnLigne } from "./reseau/connexion.js";
import { EntreeEnLigne, Salon } from "./reseau/Salon.js";
import { PartieEnLigne } from "./reseau/PartieEnLigne.js";

type Ecran =
  | { readonly quoi: "accueil" }
  | { readonly quoi: "en-ligne" }
  | {
      readonly quoi: "partie";
      readonly tournoi: Tournoi;
      readonly tables: Tables;
      /** Numéro d'ordre de la cave : sert de clé de remontage à la scène. */
      readonly numero: number;
    }
  | { readonly quoi: "campagne" }
  | {
      readonly quoi: "comptoir";
      readonly comptoir: Comptoir;
      readonly tournoi: Tournoi;
      readonly tables: Tables;
      readonly numero: number;
      /** Le relevé cumulé des caves déjà jouées à ce comptoir. */
      readonly releve: Statistiques;
    }
  | { readonly quoi: "bac" };

/**
 * Les tables d'équilibre, chargées une seule fois.
 *
 * Le hash du RuleSet est vérifié au chargement : des tables calculées pour d'autres
 * règles donneraient une IA plausible et fausse, ce qui est pire qu'une IA absente.
 */
let enMemoire: Tables | null = null;

async function tables(): Promise<Tables> {
  if (enMemoire) return enMemoire;
  const brut = await import("@zinc421/shared/ai/tables/maison-2026-07.json");
  enMemoire = chargerTables(brut.default ?? brut, REGLES_MAISON.id);
  return enMemoire;
}

/** Le coffre, monté une fois : IndexedDB ouvre une base, pas à chaque rendu. */
const coffre: Coffre = coffreNavigateur();

/**
 * Ce que propose le bouton de fin de cave, en soirée libre.
 *
 * Déduit de l'étape COURANTE, sans appeler `caveSuivante` : celle-ci lève tant que
 * la cave n'a pas de perdant, et le libellé se calcule à chaque rendu — y compris
 * en pleine partie. C'est ce qui a fait planter l'écran au démarrage.
 */
function suiteDe(tournoi: Tournoi, textes: Textes): string {
  switch (tournoi.etape) {
    case "CAVE": return textes.enchainerLaRecave;
    // Après la recave, la finale n'a lieu que si les deux perdants diffèrent : on
    // ne promet donc pas une cave des caves qui pourrait ne pas se jouer.
    case "RECAVE": return textes.laSuite;
    default: return textes.voirQuiPaie;
  }
}

/**
 * Reporte un renommage dans la TABLE de la soirée.
 *
 * ⚠️ La cave porte des sièges, la table porte des places — et la cave des caves n'a
 * que deux sièges. Sans `participants` pour faire la correspondance, renommer en
 * finale rebaptiserait quelqu'un d'autre. Sans ce report, le nom retomberait à sa
 * valeur d'origine à la cave suivante.
 */
function renommerALaTable(tournoi: Tournoi, siege: number, nom: string): Tournoi {
  const place = tournoi.participants[siege];
  const propre = nomPropre(nom);
  if (place === undefined || propre.length === 0) return tournoi;
  return {
    ...tournoi,
    table: tournoi.table.map((p, i) => (i === place ? { ...p, nom: propre } : p)),
    partie: {
      ...tournoi.partie,
      joueurs: tournoi.partie.joueurs.map(
        (j) => (j.siege === siege ? { ...j, nom: propre } : j)),
    },
  };
}

export function App() {
  const textes = useTextes();
  const [ecran, setEcran] = useState<Ecran>({ quoi: "accueil" });
  const [total, setTotal] = useState(4);
  const [humains, setHumains] = useState<readonly string[]>(["Toi"]);
  const [niveau, setNiveau] = useState<Niveau>(4);
  const [enPreparation, setEnPreparation] = useState(false);
  const [parties, setParties] = useState(0);
  const [nomEnLigne, setNomEnLigne] = useState("Toi");
  const [codeSaisi, setCodeSaisi] = useState("");
  const enLigne = usePartieEnLigne();
  const [probleme, setProbleme] = useState<string | null>(null);

  // --- la campagne ------------------------------------------------------------
  const [progression, setProgression] = useState<Progression>(progressionNeuve);
  const [derniereIssue, setDerniereIssue] = useState<string | null>(null);
  const [stockageMuet, setStockageMuet] = useState(false);

  /**
   * Le son, et l'haptique avec lui.
   *
   * Actif par défaut : un 421 muet n'est pas un 421. Le navigateur ne laissera de
   * toute façon rien sonner avant le premier geste du joueur, donc personne n'est
   * surpris au chargement.
   */
  const [reprise, setReprise] = useState<EnCours | null>(null);

  /**
   * La partie en cours, telle que l'écran de partie la voit.
   *
   * Dans une **ref** et non dans un état : elle change à chaque révision — plusieurs
   * fois par seconde pendant un tour d'adversaire — et la remonter dans `useState`
   * ferait rendre tout l'arbre à chaque dé qui bouge, alors que personne ne l'affiche.
   */
  const enCours = useRef<EnCours | null>(null);

  const [son, setSon] = useState(true);
  const basculerSon = useCallback((actif: boolean) => {
    setSon(actif);
    void sauverSon(coffre, actif);
  }, []);

  // Le coffre est asynchrone : on affiche une campagne vierge, puis la vraie dès
  // qu'elle arrive. Attendre le disque pour afficher un menu serait le mauvais
  // échange — et `chargerProgression` ne lève jamais.
  useEffect(() => {
    let vivant = true;
    void chargerProgression(coffre).then((retenue) => {
      if (vivant) setProgression(retenue);
    });
    void chargerSon(coffre).then((actif) => {
      if (vivant) setSon(actif);
    });
    void chargerReprise(coffre).then((retrouvee) => {
      if (vivant) setReprise(retrouvee);
    });
    return () => { vivant = false; };
  }, []);

  /**
   * Sauvegarde de la partie en cours (§ 5.4).
   *
   * Déclenchée au passage en arrière-plan et à la fermeture, jamais à chaque coup :
   * IndexedDB écrit sur le disque, et le faire soixante fois par manche coûterait
   * plus cher que la scène 3D. `pagehide` double `visibilitychange` parce que sur iOS
   * une page peut être mise en cache sans jamais devenir « hidden ».
   */
  useEffect(() => {
    const sauver = () => {
      const etat = enCours.current;
      if (etat) void sauverReprise(coffre, etat);
    };
    const auChangement = () => {
      if (document.visibilityState === "hidden") sauver();
    };
    document.addEventListener("visibilitychange", auChangement);
    window.addEventListener("pagehide", sauver);
    return () => {
      document.removeEventListener("visibilitychange", auChangement);
      window.removeEventListener("pagehide", sauver);
    };
  }, []);

  const commencer = useCallback(() => {
    setEnPreparation(true);
    setProbleme(null);
    void tables()
      .then((chargees) => {
        setParties((n) => n + 1);
        setEcran({
          quoi: "partie",
          tournoi: ouvrirTournoi(placesDeLaTable(humains, total, niveau)),
          tables: chargees,
          numero: parties + 1,
        });
      })
      .catch((erreur: unknown) => {
        setProbleme(erreur instanceof Error ? erreur.message : String(erreur));
      })
      .finally(() => { setEnPreparation(false); });
  }, [humains, total, niveau, parties]);

  const auMenu = useCallback(() => { setEcran({ quoi: "accueil" }); }, []);
  const aLaTournee = useCallback(() => { setEcran({ quoi: "campagne" }); }, []);

  /** La partie est finie : il n'y a plus rien à reprendre. */
  const oublier = useCallback(() => {
    enCours.current = null;
    setReprise(null);
    void oublierReprise(coffre);
  }, []);

  /**
   * On quitte volontairement : on sauve TOUT DE SUITE.
   *
   * Attendre le passage en arrière-plan suffirait sur téléphone, mais quelqu'un qui
   * ferme l'onglet depuis le menu n'y repassera pas — et il aurait pourtant vu le
   * bouton « Reprendre » juste avant.
   */
  const quitterEnGardant = useCallback((retour: () => void) => {
    const etat = enCours.current;
    if (etat) {
      setReprise(etat);
      void sauverReprise(coffre, etat);
    }
    retour();
  }, []);

  /** Rouvre la partie laissée en plan, exactement où elle en était. */
  const reprendre = useCallback(() => {
    const etat = reprise;
    if (!etat) return;
    setEnPreparation(true);
    setProbleme(null);
    void tables()
      .then((chargees) => {
        setParties((n) => n + 1);
        if (etat.quoi === "comptoir" && etat.comptoir !== undefined) {
          setEcran({
            quoi: "comptoir",
            comptoir: comptoirDe(etat.comptoir),
            tournoi: etat.tournoi,
            tables: chargees,
            numero: parties + 1,
            // Le relevé sauvé est le CUMUL, cave interrompue comprise. L'écran repart
            // d'un compteur neuf : ce qu'il ajoutera sera donc bien ce qui reste à
            // compter, sans rien perdre ni rien compter deux fois.
            releve: etat.releve,
          });
        } else {
          setEcran({
            quoi: "partie", tournoi: etat.tournoi, tables: chargees, numero: parties + 1,
          });
        }
      })
      .catch((erreur: unknown) => {
        setProbleme(erreur instanceof Error ? erreur.message : String(erreur));
      })
      .finally(() => { setEnPreparation(false); });
  }, [reprise, parties]);

  /** Ouvre un comptoir : la table est celle du lieu, jamais celle de l'accueil. */
  const jouerComptoir = useCallback((numeroComptoir: number) => {
    const comptoir = comptoirDe(numeroComptoir);
    setEnPreparation(true);
    setProbleme(null);
    setDerniereIssue(null);
    void tables()
      .then((chargees) => {
        setParties((n) => n + 1);
        setEcran({
          quoi: "comptoir",
          comptoir,
          tournoi: ouvrirTournoi(
            // Les noms des adversaires sont TRADUITS : ils viennent du dictionnaire,
            // et ils atterrissent dans l'état de la partie. Une soirée commencée en
            // français garde ses noms français, et c'est voulu.
            placesDuComptoir(
              humains[0] ?? "", comptoir,
              (n) => textes.adversaireNom(profilDe(n).cle), textes.tonNomPlaceholder),
            undefined, comptoir.soiree ? "SOIREE" : "SIMPLE"),
          tables: chargees,
          numero: parties + 1,
          releve: statistiquesVides(),
        });
      })
      .catch((erreur: unknown) => {
        setProbleme(erreur instanceof Error ? erreur.message : String(erreur));
        setEcran({ quoi: "campagne" });
      })
      .finally(() => { setEnPreparation(false); });
  }, [humains, parties, textes]);

  /**
   * Le comptoir est plié : on enregistre, on sauve, on revient à la tournée.
   *
   * L'enregistrement passe par `enregistrer`, qui refuse un comptoir fermé — c'est
   * le garde-fou qui empêche un bug d'écran d'ouvrir toute la campagne d'un coup.
   */
  const cloreComptoir = useCallback((
    comptoir: Comptoir, paye: number | null, releve: Statistiques,
  ) => {
    const issue = issueDeComptoir(comptoir, paye, releve, textes);
    // ⚠️ Rien de tout ceci ne passe par un updater de `setState`. React rappelle
    // volontairement les updaters deux fois en mode strict : y écrire dans le coffre
    // sauvegarderait deux fois, et y appeler `setProbleme` reviendrait à mettre à
    // jour un composant pendant qu'un autre rend. C'est le même piège que `agir()`
    // au lot 3, sous une autre forme.
    let apres: Progression;
    try {
      apres = enregistrer(progression, comptoir.numero, {
        gagne: issue.gagne, stats: releve,
      });
    } catch (erreur) {
      setProbleme(erreur instanceof Error ? erreur.message : String(erreur));
      setEcran({ quoi: "campagne" });
      return;
    }
    setProgression(apres);
    oublier();
    void sauverProgression(coffre, apres).then((ecrit) => { setStockageMuet(!ecrit); });
    setDerniereIssue(issue.message);
    setEcran({ quoi: "campagne" });
  }, [progression, oublier, textes]);

  if (ecran.quoi === "en-ligne") {
    // Trois états d'une même liaison : on entre, on attend, on joue. Le statut
    // vient de la connexion elle-même, jamais d'un drapeau tenu à côté — c'est ce
    // qui évite un écran de salon devant une partie déjà commencée.
    if (enLigne.statut === "partie") {
      return <PartieEnLigne enLigne={enLigne} son={son} onSon={basculerSon} />;
    }
    if (enLigne.statut === "salon") return <Salon enLigne={enLigne} />;
    return (
      <EntreeEnLigne
        enLigne={enLigne}
        nom={nomEnLigne}
        code={codeSaisi}
        onNom={setNomEnLigne}
        onCode={setCodeSaisi}
        onQuitter={() => { enLigne.quitter(); auMenu(); }}
      />
    );
  }
  if (ecran.quoi === "bac") return <BacASable onQuitter={auMenu} />;

  if (ecran.quoi === "campagne") {
    return (
      <>
        <Campagne
          progression={progression}
          nom={humains[0] ?? ""}
          onNom={(nom) => { setHumains((noms) => [nom, ...noms.slice(1)]); }}
          onJouer={jouerComptoir}
          onQuitter={auMenu}
          derniere={derniereIssue}
          stockageMuet={stockageMuet}
          enPreparation={enPreparation}
        />
        {probleme && <p className="alerte" role="alert">{probleme}</p>}
      </>
    );
  }

  if (ecran.quoi === "comptoir") {
    const { comptoir, tournoi } = ecran;
    const fini = tournoi.etape === "FINI";
    return (
      <Partie
        key={`${ecran.numero}-${tournoi.etape}`}
        depart={tournoi.partie}
        tables={ecran.tables}
        etape={titreDeCave(comptoir, tournoi, textes)}
        libelleSuite={libelleSuiteComptoir(tournoi, textes)}
        monSiege={MA_PLACE}
        son={son}
        onSon={basculerSon}
        onRenomme={(siege, nom) => {
          setEcran({ ...ecran, tournoi: renommerALaTable(tournoi, siege, nom) });
        }}
        onEtat={(partie, releveCave) => {
          enCours.current = {
            quoi: "comptoir",
            comptoir: comptoir.numero,
            tournoi: { ...tournoi, partie },
            releve: additionner(ecran.releve, releveCave),
          };
        }}
        onSuite={(partieFinie, releve) => {
          // Le relevé rendu par l'écran ne couvre que la cave qui vient de se jouer
          // — l'écran est remonté à chaque cave (clé `numero-etape`), donc son
          // compteur repart de zéro. Une soirée en compte trois : on additionne ici.
          const cumul = additionner(ecran.releve, releve);
          if (fini) { cloreComptoir(comptoir, tournoi.paye, cumul); return; }

          const suivant = caveSuivante({ ...tournoi, partie: partieFinie });
          if (suivant.etape === "FINI") {
            cloreComptoir(comptoir, suivant.paye, cumul);
            return;
          }
          setEcran({ ...ecran, tournoi: suivant, releve: cumul });
        }}
        onQuitter={() => quitterEnGardant(aLaTournee)}
      />
    );
  }

  if (ecran.quoi === "partie") {
    const tournoi = ecran.tournoi;
    const fini = tournoi.etape === "FINI";
    const paye = tournoi.paye === null ? null : tournoi.table[tournoi.paye];
    return (
      // Une clé par cave : la scène repart d'un tapis propre plutôt que de garder
      // les dés de la cave précédente là où ils étaient tombés.
      <Partie
        key={`${ecran.numero}-${tournoi.etape}`}
        depart={tournoi.partie}
        tables={ecran.tables}
        etape={fini && paye
          ? textes.paieSaTourneePhrase(paye.nom)
          : nomDeLEtape(tournoi.etape, textes)}
        libelleSuite={fini ? textes.uneAutreSoiree : suiteDe(tournoi, textes)}
        son={son}
        onSon={basculerSon}
        onEtat={(partie, releve) => {
          enCours.current = { quoi: "soiree", tournoi: { ...tournoi, partie }, releve };
        }}
        onRenomme={(siege, nom) => {
          setEcran({ ...ecran, tournoi: renommerALaTable(tournoi, siege, nom) });
        }}
        onSuite={(partieFinie) => {
          if (fini) { oublier(); auMenu(); return; }
          // La cave s'est jouée dans l'écran de partie : c'est elle, et non l'état
          // de départ que garde cet écran, qui porte le perdant.
          setEcran({
            ...ecran,
            tournoi: caveSuivante({ ...tournoi, partie: partieFinie }),
          });
        }}
        onQuitter={() => quitterEnGardant(auMenu)}
      />
    );
  }

  return (
    <>
      <Accueil
        total={total}
        humains={humains}
        niveau={niveau}
        onTotal={(n) => {
          setTotal(n);
          // On ne laisse jamais plus d'humains que de sièges : la table serait
          // refusée au démarrage, et le joueur ne saurait pas pourquoi.
          setHumains((noms) => (noms.length > n ? noms.slice(0, n) : noms));
        }}
        onHumains={setHumains}
        onNiveau={setNiveau}
        onCommencer={commencer}
        onCampagne={aLaTournee}
        reprise={reprise === null ? null : libelleReprise(reprise, textes)}
        onReprendre={reprendre}
        onEnLigne={() => setEcran({ quoi: "en-ligne" })}
        onBacASable={() => setEcran({ quoi: "bac" })}
        son={son}
        onSon={basculerSon}
        enPreparation={enPreparation}
      />
      {probleme && <p className="alerte" role="alert">{probleme}</p>}
    </>
  );
}
